import type { Coverage, CoveragePlan } from "./types";

export type CoverageFieldData = {
  label: string;
  value?: string;
};

export type MappedPlan = {
  name: string;
  fields: CoverageFieldData[];
};

export type MappedCoverage = {
  id: number;
  name: string;
  plans: MappedPlan[];
};

const LABELS = {
  contributionStructure: "Contribution Structure",
  fundingArrangement: "Funding Arrangement",
  currentCommission: "Current Commission",
  requestedCommission: "Requested Commission",
  planDesign: "Centro Benchmarked Plan Design",
  alternatePlans: "Alternate Plans",
  additionalNotes: "Additional Notes",
  ficaOption: "FICA Option",
  ficaBillable: "FICA Billable",
  enrolledLives: "Enrolled Lives",
  rateType: "Rate Type",
  enrolledW2Employees: "Enrolled W-2 Employees",
  americanWithDisabilitiesAct: "American with Disabilities Act (ADA)",
  state: "State",
  planType: "Plan Type",
};

// Helpers

function contributionStructure(plan: CoveragePlan): string {
  const structure = plan.contributionStructure ?? "";
  const type = plan.contributionType ? ` - ${plan.contributionType}` : "";
  const value = plan.contributionValue ? `: ${plan.contributionValue}` : "";

  return `${structure}${type}${value}`;
}

function fundingArrangement(plan: CoveragePlan): string {
  const arrangement = plan.fundingArrangement ?? "";
  const alternateFundedType = plan.alternateFundedType
    ? ` - ${plan.alternateFundedType}`
    : "";

  return `${arrangement}${alternateFundedType}`;
}

function state(plan: CoveragePlan): string {
  const states = plan.states;
  if (!states) return "";

  return Object.entries(states)
    .map(
      ([key, val]) =>
        key + (val ? `: ${[...new Set(val.filter(Boolean))]}` : ""),
    )
    .join("\n");
}

// Builder

class FieldsDataBuilder {
  private fields: CoverageFieldData[] = [];

  addCustom(label: string, value?: string): FieldsDataBuilder {
    this.fields.push({ label, value });
    return this;
  }

  add(
    plan: CoveragePlan,
    item: Exclude<keyof CoveragePlan, "states"> | CoverageFieldData,
  ): FieldsDataBuilder {
    let fieldsData: CoverageFieldData;
    if (typeof item === "string") {
      fieldsData = {
        label: LABELS[item as keyof typeof LABELS] || "",
        value: plan[item] as string | undefined,
      };
    } else {
      fieldsData = item;
    }
    this.fields.push(fieldsData);
    return this;
  }

  addAll(
    plan: CoveragePlan,
    data: (Exclude<keyof CoveragePlan, "states"> | CoverageFieldData)[],
  ): FieldsDataBuilder {
    for (const item of data) {
      this.add(plan, item);
    }
    return this;
  }

  get result(): CoverageFieldData[] {
    return this.fields;
  }
}

function buildFieldsData(): FieldsDataBuilder {
  return new FieldsDataBuilder();
}

// Coverage-type mappers

function mapDentalPlans(
  plan: CoveragePlan,
  coverage: Coverage,
): CoverageFieldData[] {
  const builder = buildFieldsData();
  const plans = coverage.details.plans;
  const isSingle = plans.length === 1;

  if (isSingle) {
    builder.addCustom(LABELS.planType, plan.name);

    if (coverage.details.planType !== "DHMO") {
      builder.addCustom(LABELS.fundingArrangement, fundingArrangement(plan));
    }
  } else {
    builder.addCustom(LABELS.fundingArrangement, fundingArrangement(plan));
  }

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "enrolledLives");
  } else {
    builder.add(plan, "planDesign");
  }

  builder.add(plan, {
    label: LABELS.contributionStructure,
    value: contributionStructure(plan),
  });

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "currentCommission");
  }

  builder.addAll(plan, [
    "requestedCommission",
    "alternatePlans",
    "additionalNotes",
  ]);

  return builder.result;
}

function mapVisionPlans(
  plan: CoveragePlan,
  coverage: Coverage,
): CoverageFieldData[] {
  const builder = buildFieldsData();
  const plans = coverage.details.plans;
  const isSingle = plans.length === 1;

  if (isSingle) {
    builder.addCustom(LABELS.planType, plan.name);
  }

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "enrolledLives");
  } else {
    builder.add(plan, "planDesign");
  }

  builder.addAll(plan, [
    {
      label: LABELS.contributionStructure,
      value: contributionStructure(plan),
    },
    {
      label: LABELS.fundingArrangement,
      value: fundingArrangement(plan),
    },
  ]);

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "currentCommission");
  }

  builder.addAll(plan, [
    "requestedCommission",
    "alternatePlans",
    "additionalNotes",
  ]);

  return builder.result;
}

// Display value stored for a "Yes" FICA Matching selection (see rfp-core
// COVERAGE_FORM.FICA_MATCHING_YES). Kept local to avoid a rfp-core dependency.
const FICA_MATCHING_YES_DISPLAY = "Yes";

function mapSTDPlans(
  plan: CoveragePlan,
  coverage: Coverage,
): CoverageFieldData[] {
  const builder = buildFieldsData();
  const plans = coverage.details.plans;
  const isSingle = plans.length === 1;

  if (isSingle) {
    builder.addCustom(LABELS.planType, plan.name);
  }

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "enrolledLives");
  } else {
    builder.add(plan, "planDesign");
  }

  builder.addAll(plan, [
    {
      label: LABELS.contributionStructure,
      value: contributionStructure(plan),
    },
    "ficaOption",
  ]);

  // FICA Billable applies only when FICA Matching is Yes; omit the row otherwise
  // (guard on the matching selection too, in case a stale response carries a
  // billable value under a non-Yes plan).
  if (plan.ficaBillable && plan.ficaOption === FICA_MATCHING_YES_DISPLAY) {
    builder.add(plan, "ficaBillable");
  }

  builder.add(plan, {
    label: LABELS.fundingArrangement,
    value: fundingArrangement(plan),
  });

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "currentCommission");
  }

  builder.addAll(plan, [
    "requestedCommission",
    "alternatePlans",
    "additionalNotes",
  ]);

  return builder.result;
}

function mapLTDPlans(
  plan: CoveragePlan,
  coverage: Coverage,
): CoverageFieldData[] {
  const builder = buildFieldsData();

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "enrolledLives");
  } else {
    builder.add(plan, "planDesign");
  }

  builder.addAll(plan, [
    {
      label: LABELS.contributionStructure,
      value: contributionStructure(plan),
    },
    "rateType",
  ]);

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "currentCommission");
  }

  builder.addAll(plan, [
    "requestedCommission",
    "alternatePlans",
    "additionalNotes",
  ]);

  return builder.result;
}

function mapBasicLifeAdndPlans(
  plan: CoveragePlan,
  coverage: Coverage,
): CoverageFieldData[] {
  const builder = buildFieldsData();

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "enrolledLives");
  } else {
    builder.add(plan, "planDesign");
  }

  builder.add(plan, {
    label: LABELS.contributionStructure,
    value: contributionStructure(plan),
  });

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "currentCommission");
  }

  builder.addAll(plan, [
    "requestedCommission",
    "alternatePlans",
    "additionalNotes",
  ]);

  return builder.result;
}

function mapVoluntaryLifePlans(
  plan: CoveragePlan,
  coverage: Coverage,
): CoverageFieldData[] {
  const builder = buildFieldsData();

  if (coverage.isCurrentCoverage) {
    builder.addAll(plan, ["enrolledLives", "currentCommission"]);
  } else {
    builder.add(plan, "planDesign");
  }

  builder.addAll(plan, [
    "requestedCommission",
    "alternatePlans",
    "additionalNotes",
  ]);

  return builder.result;
}

function mapCommonPlans(
  plan: CoveragePlan,
  coverage: Coverage,
): CoverageFieldData[] {
  const builder = buildFieldsData();

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "currentCommission");
  } else {
    builder.add(plan, "planDesign");
  }

  builder.addAll(plan, [
    "requestedCommission",
    "alternatePlans",
    "additionalNotes",
  ]);

  return builder.result;
}

function mapWholeLifePlans(
  plan: CoveragePlan,
  coverage: Coverage,
): CoverageFieldData[] {
  const builder = buildFieldsData();

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "currentCommission");
  }

  builder.addAll(plan, [
    "requestedCommission",
    "alternatePlans",
    {
      label: LABELS.additionalNotes,
      value: plan.additionalNotes || plan.comments,
    },
  ]);

  return builder.result;
}

function mapIndividualDisabilityPlans(
  plan: CoveragePlan,
  coverage: Coverage,
): CoverageFieldData[] {
  const builder = buildFieldsData();

  builder.add(plan, {
    label: LABELS.contributionStructure,
    value: contributionStructure(plan),
  });

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "currentCommission");
  }

  builder.addAll(plan, [
    "requestedCommission",
    "alternatePlans",
    "additionalNotes",
  ]);

  return builder.result;
}

function mapEmployeeAssistanceProgramPlans(
  plan: CoveragePlan,
): CoverageFieldData[] {
  return [
    {
      label: LABELS.alternatePlans,
      value: plan.alternatePlans,
    },
    {
      label: LABELS.additionalNotes,
      value: plan.additionalNotes,
    },
  ];
}

function mapFamilyMedicalLeaveAdministrationPlans(
  plan: CoveragePlan,
  coverage: Coverage,
): CoverageFieldData[] {
  const builder = buildFieldsData();

  builder.addAll(plan, [
    {
      label: LABELS.enrolledW2Employees,
      value: plan["W-2EmployeesConfirmedWithZipCodes"],
    },
    {
      label: LABELS.americanWithDisabilitiesAct,
      value: plan.americanWithDisabilitiesAct,
    },
  ]);

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "currentCommission");
  }

  builder.addAll(plan, [
    "requestedCommission",
    "alternatePlans",
    "additionalNotes",
  ]);

  return builder.result;
}

function mapStateDisabilityAndLeavesPlans(
  plan: CoveragePlan,
  coverage: Coverage,
): CoverageFieldData[] {
  const builder = buildFieldsData();

  builder.addAll(plan, [
    "enrolledLives",
    {
      label: LABELS.state,
      value: state(plan),
    },
  ]);

  if (coverage.isCurrentCoverage) {
    builder.add(plan, "currentCommission");
  }

  builder.addAll(plan, [
    "requestedCommission",
    "alternatePlans",
    "additionalNotes",
  ]);

  return builder.result;
}

// Registry

const MAPPERS_BY_COVERAGE_TYPE: Record<
  string,
  (plan: CoveragePlan, coverage: Coverage) => CoverageFieldData[]
> = {
  Dental: mapDentalPlans,
  "Dental PPO": mapDentalPlans,
  "Dental HMO": mapDentalPlans,
  Vision: mapVisionPlans,
  "Short Term Disability": mapSTDPlans,
  "Long Term Disability": mapLTDPlans,
  "Basic Life/AD&D": mapBasicLifeAdndPlans,
  "Voluntary Life/AD&D": mapVoluntaryLifePlans,
  "Accident Insurance": mapCommonPlans,
  "Critical Illness Insurance": mapCommonPlans,
  "Hospital Indemnity Insurance": mapCommonPlans,
  "Cancer Insurance": mapCommonPlans,
  "Whole Life": mapWholeLifePlans,
  "Individual Disability Insurance": mapIndividualDisabilityPlans,
  "Employee Assistance Program": mapEmployeeAssistanceProgramPlans,
  "Family Medical Leave Administration":
    mapFamilyMedicalLeaveAdministrationPlans,
  "State Disability & Leaves": mapStateDisabilityAndLeavesPlans,
};

/**
 * Maps a coverage's plans to display fields based on the coverage type.
 * Returns null if the coverage type has no registered mapper.
 */
export function mapCoveragePlans(coverage: Coverage): MappedPlan[] | null {
  const fieldsMapper = MAPPERS_BY_COVERAGE_TYPE[coverage.name];
  if (!fieldsMapper) {
    return null;
  }

  return coverage.details.plans.map((plan) => ({
    name: plan.name,
    fields: fieldsMapper(plan, coverage),
  }));
}

/**
 * Maps an array of coverages to MappedCoverage[], filtering out
 * coverages with no registered mapper.
 */
export function mapCoverages(coverages?: Coverage[] | null): MappedCoverage[] {
  return (
    coverages
      ?.map((coverage) => {
        const plans = mapCoveragePlans(coverage);

        return {
          plans,
          id: coverage.id,
          name: coverage.name,
        };
      })
      .filter((c): c is MappedCoverage => c.plans !== null) ?? []
  );
}
